import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, ShoppingBag, Layers, Inbox, FileText, Menu as MenuIcon, Settings as SettingsIcon, Mail, MessageSquare, LogOut, ChevronDown, Users, ShoppingCart, ExternalLink } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useSite } from '../../context/SiteContext';
import { CurrencySwitcher } from '../CurrencySwitcher';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../ui/sheet';

const LINKS = [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/admin/products', label: 'Products', icon: ShoppingBag },
    { to: '/admin/collections', label: 'Collections', icon: Layers },
    { to: '/admin/requests', label: 'Requests', icon: Inbox },
    { to: '/admin/audience', label: 'Audience', icon: Users },
    { to: '/admin/carts', label: 'Carts', icon: ShoppingCart },
    { to: '/admin/messages', label: 'Messages', icon: MessageSquare },
    { to: '/admin/pages', label: 'Pages', icon: FileText },
    { to: '/admin/navigation', label: 'Navigation', icon: MenuIcon },
    { to: '/admin/settings', label: 'Settings', icon: SettingsIcon },
    { to: '/admin/newsletter', label: 'Newsletter', icon: Mail },
];

function Brand({ settings }) {
    return (
        <div className="flex-shrink-0 h-16 px-5 flex items-center gap-3 border-b border-[rgba(90,36,50,0.08)]">
            {settings?.logo_url
                ? <img src={settings.logo_url} alt="by anfal" className="h-9 w-auto" />
                : <span className="flex items-center justify-center w-9 h-9 rounded-lg font-serif text-white" style={{ backgroundColor: 'var(--brand-burgundy)' }}>a</span>}
            <div className="min-w-0">
                <p className="font-serif text-base leading-tight truncate" style={{ color: 'var(--brand-burgundy)' }}>{settings?.brand_name || 'by anfal'}</p>
                <p className="uppercase font-medium leading-none mt-0.5" style={{ color: 'var(--ink-muted)', fontSize: '0.6rem', letterSpacing: '0.18em' }}>Atelier Admin</p>
            </div>
        </div>
    );
}

function NavItems({ onNavigate }) {
    return (
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
            {LINKS.map((l) => (
                <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.end}
                    onClick={onNavigate}
                    data-testid={`admin-nav-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                        `group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium min-h-[44px] transition-colors duration-200 ${
                            isActive
                                ? 'bg-[var(--brand-rose)] text-[var(--brand-burgundy)]'
                                : 'text-[var(--ink)] hover:bg-[var(--brand-rose)]/50 hover:text-[var(--brand-burgundy)]'
                        }`
                    }
                >
                    {({ isActive }) => (
                        <>
                            {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full" style={{ backgroundColor: 'var(--brand-burgundy)' }} />}
                            <l.icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={isActive ? 2.4 : 1.8} />
                            <span className="truncate">{l.label}</span>
                        </>
                    )}
                </NavLink>
            ))}
        </nav>
    );
}

export default function AdminLayout() {
    const { admin, logout } = useAdminAuth();
    const { settings } = useSite();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileNav, setMobileNav] = useState(false);

    const onLogout = () => { logout(); navigate('/admin/login'); };
    const initial = (admin?.name || admin?.email || 'A').trim().charAt(0).toUpperCase();

    const active = [...LINKS].sort((a, b) => b.to.length - a.to.length)
        .find((l) => location.pathname === l.to || (!l.end && location.pathname.startsWith(l.to)));
    const pageTitle = active?.label || 'Admin';

    return (
        <div className="w-full min-h-[100dvh] lg:h-screen lg:flex lg:overflow-hidden" style={{ backgroundColor: 'var(--brand-cream, #FBF7F7)' }} data-testid="admin-layout">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:flex w-64 flex-shrink-0 flex-col bg-white border-r border-[rgba(90,36,50,0.10)] h-full">
                <Brand settings={settings} />
                <NavItems />
                <a
                    href="/"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="admin-view-site-link"
                    className="flex-shrink-0 flex items-center gap-2 px-5 h-14 border-t border-[rgba(90,36,50,0.08)] text-xs uppercase tracking-[0.14em] transition-colors hover:bg-[var(--brand-rose)]/40"
                    style={{ color: 'var(--ink-muted)' }}
                >
                    <ExternalLink className="w-4 h-4" /> View Storefront
                </a>
            </aside>

            {/* Mobile Drawer */}
            <Sheet open={mobileNav} onOpenChange={setMobileNav}>
                <SheetContent side="left" className="p-0 w-[86vw] max-w-[320px] bg-white flex flex-col h-full" data-testid="admin-mobile-nav">
                    <SheetTitle className="sr-only">Admin navigation</SheetTitle>
                    <SheetDescription className="sr-only">Primary navigation for the admin panel</SheetDescription>
                    <Brand settings={settings} />
                    <NavItems onNavigate={() => setMobileNav(false)} />
                    <div className="flex-shrink-0 p-3 border-t border-[rgba(90,36,50,0.08)]">
                        <button onClick={onLogout} data-testid="admin-mobile-logout" className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm min-h-[44px] hover:bg-[var(--brand-rose)]/50 transition-colors" style={{ color: 'var(--brand-burgundy)' }}>
                            <LogOut className="w-[18px] h-[18px]" /> Sign Out
                        </button>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Content column */}
            <div className="flex flex-col min-w-0 lg:flex-1 lg:overflow-hidden lg:h-full">
                {/* Top bar */}
                <header className="flex-shrink-0 z-20 sticky top-0 lg:static flex items-center gap-3 px-4 sm:px-6 lg:px-8 h-16 bg-white/80 backdrop-blur-xl border-b border-[rgba(90,36,50,0.08)]" data-testid="admin-topbar">
                    <button
                        onClick={() => setMobileNav(true)}
                        data-testid="admin-mobile-menu-button"
                        className="lg:hidden flex items-center justify-center w-11 h-11 -ml-2 rounded-lg hover:bg-[var(--brand-rose)]/40 transition-colors"
                        aria-label="Open menu"
                    >
                        <MenuIcon className="w-5 h-5" style={{ color: 'var(--brand-burgundy)' }} />
                    </button>
                    <div className="min-w-0 flex-1">
                        <p className="uppercase leading-none" style={{ color: 'var(--ink-muted)', fontSize: '0.58rem', letterSpacing: '0.2em' }}>Atelier</p>
                        <h1 className="font-serif text-lg sm:text-xl leading-tight truncate" style={{ color: 'var(--brand-burgundy)' }} data-testid="admin-page-title">{pageTitle}</h1>
                    </div>
                    <CurrencySwitcher variant="admin" className="mr-1" />
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="admin-account-trigger"
                                className="flex items-center gap-2.5 pl-1.5 pr-2 sm:pr-3 py-1.5 rounded-full border border-[rgba(90,36,50,0.14)] hover:bg-[var(--brand-rose)]/40 transition-colors"
                            >
                                <span className="flex items-center justify-center w-8 h-8 rounded-full font-serif text-sm text-white flex-shrink-0" style={{ backgroundColor: 'var(--brand-burgundy)' }}>{initial}</span>
                                <span className="hidden sm:block text-sm max-w-[160px] truncate" style={{ color: 'var(--brand-burgundy)' }}>{admin?.name || admin?.email}</span>
                                <ChevronDown className="w-4 h-4 hidden sm:block" style={{ color: 'var(--ink-muted)' }} />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 bg-white" data-testid="admin-account-menu">
                            <DropdownMenuLabel className="font-normal">
                                <p className="text-sm font-medium" style={{ color: 'var(--brand-burgundy)' }}>{admin?.name || 'Admin'}</p>
                                <p className="text-xs truncate" style={{ color: 'var(--ink-muted)' }}>{admin?.email}</p>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem asChild className="cursor-pointer text-sm">
                                <a href="/" target="_blank" rel="noreferrer" style={{ color: 'var(--ink)' }}>
                                    <ExternalLink className="w-4 h-4 mr-2" /> View Storefront
                                </a>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={onLogout}
                                data-testid="admin-logout-button"
                                className="cursor-pointer text-sm"
                                style={{ color: 'var(--brand-burgundy)' }}
                            >
                                <LogOut className="w-4 h-4 mr-2" /> Sign Out
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </header>

                {/* Scrollable content */}
                <main className="flex-1 overflow-x-hidden lg:overflow-y-auto p-4 sm:p-6 lg:p-8" data-testid="admin-content">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
